import { getRuntimeEnv } from '../lib/env';

export type AdLockReason =
  | 'form-input'
  | 'calculator-input'
  | 'calculator-running'
  | 'installer-search'
  | 'installer-filtering'
  | 'map-active'
  | 'mobile-navigation'
  | 'modal-open'
  | 'contact-action'
  | 'quote-action';

export type AdRouteCategory = 'informational' | 'interactive' | 'conversion' | 'legal' | 'system';

const INPAGE_SCRIPT_URL = 'https://nap5k.com/tag.min.js';
const VIGNETTE_SCRIPT_URL = 'https://n6wxm.com/vignette.min.js';
const ZONE_PATTERN = /^\d+$/;

const cleanPath = (path: string) =>
  `/${path.split(/[?#]/)[0].split('/').filter(Boolean).join('/')}`;

const legalRoutes = ['/privacy', '/terms', '/disclaimer', '/advertise', '/corrections'];
const systemRoutes = ['/404', '/offline'];
const conversionRoutes = [
  '/contact',
  '/solar-assessment',
  '/installer-checklist',
  '/my-solar-plan',
];
const interactiveRoutes = [
  '/calculators',
  '/solar/calculators',
  '/solar/compare-quotes',
  '/solar-quote-checker-philippines',
  '/solar-companies',
  '/solar/installers',
];

const matchesRoute = (path: string, routes: string[]) =>
  routes.some((route) => path === route || path.startsWith(`${route}/`));

export function getAdRouteCategory(pathname: string): AdRouteCategory {
  const path = cleanPath(pathname);
  if (matchesRoute(path, systemRoutes)) return 'system';
  if (matchesRoute(path, legalRoutes)) return 'legal';
  if (matchesRoute(path, conversionRoutes)) return 'conversion';
  if (matchesRoute(path, interactiveRoutes)) return 'interactive';
  return 'informational';
}

export const isInPageEligibleRoute = (path: string) =>
  !['legal', 'system', 'conversion'].includes(getAdRouteCategory(path));

export const isVignetteEligibleRoute = (path: string) =>
  getAdRouteCategory(path) === 'informational';

const positiveNumber = (value: string | boolean | undefined, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export function getAdConfig() {
  const env = getRuntimeEnv();
  const globallyEnabled = env.VITE_ADS_ENABLED === 'true';
  const inPageZoneId = String(env.VITE_MONETAG_INPAGE_ZONE_ID ?? '').trim();
  const vignetteZoneId = String(env.VITE_MONETAG_VIGNETTE_ZONE_ID ?? '').trim();
  return {
    enabled: globallyEnabled,
    provider: 'monetag' as const,
    debug: env.VITE_AD_DEBUG === 'true' && !import.meta.env.PROD,
    interactionCooldownMs: 7000,
    formats: {
      inPagePush: {
        enabled:
          globallyEnabled &&
          env.VITE_MONETAG_INPAGE_ENABLED === 'true' &&
          ZONE_PATTERN.test(inPageZoneId),
        zoneId: inPageZoneId,
        scriptUrl: INPAGE_SCRIPT_URL,
        delayMs: positiveNumber(env.VITE_MONETAG_INPAGE_DELAY_MS, 3000),
        desktopEnabled: true,
        mobileEnabled: true,
        defaultPolicy: 'enabled' as const,
      },
      vignette: {
        enabled:
          globallyEnabled &&
          env.VITE_MONETAG_VIGNETTE_ENABLED === 'true' &&
          ZONE_PATTERN.test(vignetteZoneId),
        zoneId: vignetteZoneId,
        scriptUrl: VIGNETTE_SCRIPT_URL,
        minimumPageViews: positiveNumber(env.VITE_MONETAG_VIGNETTE_MIN_PAGE_VIEWS, 2),
        minimumSessionAgeMs: 15000,
        cooldownMinutes: positiveNumber(env.VITE_MONETAG_VIGNETTE_COOLDOWN_MINUTES, 30),
      },
    },
  };
}
